import { GridHookForm } from 'vnetwork-ui'
import useUploadFile from '@/hooks/useUploadFile'
import { STORGE_PREFIXS } from '@/modules/Contract/modules/Process/constants'
import { DOCUMENT_TYPES } from '@/modules/Employee/constants'
import { addEmployeeDocument } from '@/modules/Employee/services'
import { Attachment } from '@/modules/Upload/types'
import { ModalProps } from '@/types/shared'
import { renderFileItem } from '@/utils/function'
import { onDelete, onUpload } from '@/components/Attachment/functions'
import { SelectField } from '@/components/ui/select'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog } from 'vnetwork-ui'
import * as yup from 'yup'
import React from 'react'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface AddDocumentModalProps extends ModalProps {
    employeeId: number
    onSuccess?: () => void
}

interface FormValues {
    document_type: string
    attachments: File[] | Attachment[]
}

const schema = yup.object({
    document_type: yup.string().required('Document type is required'),
    attachments: yup.array().min(1, 'At least one file is required'),
})

const AddDocumentModal: React.FC<AddDocumentModalProps> = ({
    onClose,
    open,
    employeeId,
    onSuccess,
}) => {
    const { t } = useTranslation()

    const formControl = useForm<FormValues>({
        defaultValues: {
            document_type: '',
            attachments: [],
        },
        resolver: yupResolver(schema as any),
    })

    const { mutateAsync: addDocument, isPending } = useMutationWithHandler(
        {
            mutationFn: addEmployeeDocument,
        },
        {
            reactQueryKeys: QUERY_KEYS.EMPLOYEES,
            successMessage: t('document-added-success'),
            onSuccess: () => {
                onClose()
                onSuccess?.()
            },
        }
    )

    const { uploadAsync } = useUploadFile({
        prefix: STORGE_PREFIXS.EMPLOYEE_DOCUMENTS,
    })

    const onSubmit = async (values: FormValues) => {
        const { attachments } = await onUpload({
            oldAttachments: [],
            attachments: values.attachments as File[] | Attachment[],
            uploadAsync: uploadAsync as (
                files: File[]
            ) => Promise<{ status: string; value: Attachment }[]>,
        })

        await addDocument({
            id: employeeId,
            document_type: values.document_type,
            attachments,
        })
    }

    return (
        <Dialog
            title={t('add-employee-document')}
            onClose={onClose}
            open={open}
        >
            <GridHookForm
                formControl={formControl}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        variant: 'outlined',
                        handler: onClose,
                        disabled: isPending,
                    },
                    {
                        title: t('add-document'),
                        type: 'submit',
                        variant: 'contained',
                        loading: isPending,
                        disabled: isPending,
                    },
                ]}
                onSubmit={onSubmit}
                data={[
                    {
                        renderEditComponent: () => (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('employees.document_type')}
                                </label>
                                <Controller
                                    name="document_type"
                                    control={formControl.control}
                                    render={({ field, fieldState }) => (
                                        <>
                                            <SelectField
                                                value={field.value}
                                                onValueChange={field.onChange}
                                                options={DOCUMENT_TYPES(t)}
                                                placeholder={t(
                                                    'employees.select_document_type'
                                                )}
                                                showClearButton={false}
                                            />
                                            {fieldState.error && (
                                                <p className="text-red-500 text-xs mt-1">
                                                    {fieldState.error.message}
                                                </p>
                                            )}
                                        </>
                                    )}
                                />
                            </div>
                        ),
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            multiple: true,
                            name: 'attachments',
                            type: 'files',
                            renderFileItem: (file: File | Attachment) => {
                                return renderFileItem(file, (file) => {
                                    const newAttachments = onDelete({
                                        attachments:
                                            formControl.getValues(
                                                'attachments'
                                            ),
                                        file,
                                    })
                                    formControl.setValue(
                                        'attachments',
                                        newAttachments
                                    )
                                })
                            },
                            label: t('employees.attachments'),
                            placeholder: t('employees.upload_files'),
                        },
                        className: 'col-span-4',
                    },
                ]}
            />
        </Dialog>
    )
}

export default AddDocumentModal
